import { useMutation, useQueryClient, MutationOptions } from "react-query";
import { post, API_URL, APIResponse, APIError } from "../api";
import { Invoice, Order, Transaction } from "../@types";

export interface CompleteCartPaymentPayload {
  session_id: string;
  loan_id?: string;
  payment_option_id: string;
  card_id?: string;
  commission_source?: "wallet" | "card";
}

export interface Data {
  order: Order;
  invoice: Invoice;
  transaction: Transaction;
}

/**
 * Checkout error codes after which the customer must not pay again: the money
 * was taken, may have been taken, or another payment is still running.
 */
export const CHECKOUT_HELD_ERROR_CODES = [
  "CHECKOUT_PAYMENT_PENDING",
  "CHECKOUT_PAID_NOT_FULFILLED",
  "PAYMENT_IN_PROGRESS",
] as const;

export type CheckoutHeldReason =
  | (typeof CHECKOUT_HELD_ERROR_CODES)[number]
  /** No usable answer from the server, so we cannot say what happened. */
  | "OUTCOME_UNKNOWN";

export type CheckoutHeldDetails = {
  reason: CheckoutHeldReason;
  reference?: string;
};

/**
 * Statuses on which the server definitely refused the request before taking any
 * money: bad input, an expired session, insufficient funds, a changed cart.
 * Anything else (a 5xx, no response, a body we could not read) leaves the
 * outcome unknown.
 */
const DEFINITE_FAILURE_STATUSES = [400, 401, 403, 404, 409, 422];

/**
 * Non-null when the customer must be told not to pay again. Errs towards
 * "unknown": telling someone a payment failed when it went through invites them
 * to pay twice.
 */
export function getCheckoutHeldDetails(
  error: unknown
): CheckoutHeldDetails | null {
  if (!(error instanceof APIError)) return { reason: "OUTCOME_UNKNOWN" };

  const details = error.responseData?.data as
    | { error_code?: string; reference?: string }
    | undefined;
  const code = details?.error_code;

  if (
    code &&
    (CHECKOUT_HELD_ERROR_CODES as readonly string[]).includes(code)
  ) {
    return {
      reason: code as CheckoutHeldReason,
      reference: details?.reference,
    };
  }

  if (
    error.isNetworkError ||
    error.isUnreadableResponse ||
    typeof error.status !== "number" ||
    !DEFINITE_FAILURE_STATUSES.includes(error.status)
  ) {
    return { reason: "OUTCOME_UNKNOWN" };
  }

  return null;
}

export function useCompleteCartPayment(
  options?: MutationOptions<
    APIResponse<Data>,
    APIError,
    CompleteCartPaymentPayload
  >
) {
  const queryClient = useQueryClient();

  const { mutate, isLoading, isError, error, data } = useMutation<
    APIResponse<Data>,
    APIError,
    CompleteCartPaymentPayload
  >(
    (payload: CompleteCartPaymentPayload) => {
      return post(
        `${API_URL}/commerce/cart/checkout/payment/complete`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
    },
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries("cart");
        queryClient.invalidateQueries("customer-orders");
        options?.onSuccess?.(...args);
      },
      // On failure too: a held or unconfirmed payment may still have moved money.
      onSettled: (...args) => {
        queryClient.refetchQueries(["transactionHistory", "profile"]);
        options?.onSettled?.(...args);
      },
    }
  );

  return {
    completeCartPayment: mutate,
    completingCartPayment: isLoading,
    isCompleteCartPaymentError: isError,
    completeCartPaymentError: error,
    completeCartPaymentData: data,
  };
}
